import { format as formatDate, formatDistance, formatRelative, isDate } from "date-fns";
import { enAU, ja } from "date-fns/locale";
import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import translationEn from "./en.json";
import translationJa from "./ja.json";
import { LOCALE_URL_PARAM, persistLocale, resolveLocaleFromWindow } from "./resolveLocale";

const dateFnsLocales: Record<string, typeof enAU | typeof ja> = { en: enAU, ja };
const initialLocale = resolveLocaleFromWindow();

// If the locale came from a URL `?lang=`, persist it so the choice survives navigation.
if (typeof window !== "undefined" && new URLSearchParams(window.location.search).get(LOCALE_URL_PARAM)) {
  persistLocale(initialLocale);
}

i18n.use(initReactI18next).init({
  resources: {
    en: { translation: translationEn },
    ja: { translation: translationJa },
  },
  lng: initialLocale,
  fallbackLng: "en",
  interpolation: {
    escapeValue: false, // react already safes from xss
  },
});

// i18next v26 removed interpolation.format — use the Formatter service instead.
const { formatter } = i18n.services;
const addDateFormatter = (name: string, fn: (value: Date, locale: (typeof dateFnsLocales)[string]) => string) => {
  formatter?.add(name, (value: unknown, lng: string | undefined) => {
    if (isDate(value) && lng) {
      const locale = dateFnsLocales[lng] ?? enAU;
      return fn(value, locale);
    }
    return String(value);
  });
};

addDateFormatter("short", (v, locale) => formatDate(v, "yyyy/MM/dd", { locale }));
addDateFormatter("long", (v, locale) => formatDate(v, "yyyy/MM/dd HH:mm", { locale }));
addDateFormatter("full", (v, locale) => formatDate(v, "yyyy/MM/dd HH:mm:ss", { locale }));
addDateFormatter("relative", (v, locale) => formatRelative(v, new Date(), { locale }));
addDateFormatter("ago", (v, locale) => formatDistance(v, new Date(), { locale, addSuffix: true }));
